import React, { useState } from 'react';
import { Spin, Form, Select, Popconfirm } from 'antd';
import {
    PlusOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    PaperClipOutlined,
    SendOutlined,
    FileOutlined,
    EditOutlined,
    DeleteOutlined,
    LinkOutlined,
} from '@ant-design/icons';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import Table from '@/components/ui/table/Table';
import AppModal from '../AppModal';
import AddUrlModal from '@/components/module/projects/detail_tab/AddUrlModal';
import RichTextEditor from '@/components/ui/RichTextEditor';
import Markdown from '@/components/ui/Markdown';
import { RefreshCwIcon } from '@/components/ui/RefreshCwIcon';
import StatusBadge from '@/components/ui/StatusBadge';
import { SprintTaskCard } from '@/components/sprint/SprintTaskCard';
import TaskDetailModal from '@/components/module/projects/task/TaskDetailModal';
import {
    PendingAttachmentTile,
    PendingLinkWrap,
} from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import {
    DetailPanelLoading,
    CreateRoleModalBody,
    CreateRoleModalFooter,
    EmptyStateBlock,
    PanelSectionLabel,
    TenantToolbarIconBtn,
    TenantToolbarRefreshSpin,
    MutedText,
} from '../style';
import {
    projectSprintTaskModalFormStyles,
    submissionRichDescriptionFormItemStyle,
    submissionRichDescriptionLabelStyle,
} from './projectTabStyles';
import { truncateFileName, formatDate, resolveAttachmentUrl, isLikelyImageAttachmentUrl } from '@hooks/projects/projectHelpers';
import { useProjectSubmissionMemos } from '@hooks/projects/useProjectSubmissionMemos';
import { useLinkedSprintTaskDetail } from '@hooks/projects/useLinkedSprintTaskDetail';

export default function ProjectSubmissionMemosTab({ projectId, project }) {
    const {
        linkedSprintTaskDetail: sprintTaskDetail,
        linkedSprintTaskDetailBugs: sprintTaskDetailBugs,
        linkedSprintTaskDetailMarkdown: sprintTaskDetailMarkdown,
        openLinkedSprintTaskDetail: openSprintTaskDetail,
        closeLinkedSprintTaskDetail: closeSprintTaskDetail,
        refreshLinkedSprintTaskDetail: refreshSprintTaskDetail,
    } = useLinkedSprintTaskDetail(projectId);

    const {
        listLoading,
        listRefetching,
        refetchMemos,

        totalMemos,
        submittedCount,
        totalLinkedTasks,
        totalAttachments,

        tableData,
        columns,

        canCreate,
        canUpdate,
        canDelete,

        createModalOpen,
        setCreateModalOpen,
        closeCreateModal,
        createMutation,

        detailModalOpen,
        selectedMemo,
        isEditing,
        openDetailModal,
        closeDetailModal,
        startEditing,
        handleDelete,
        updateMutation,
        deleteMutation,
        editForm,
        handleEditSubmit,

        form,
        taskOptions,
        tasksLoading,
        mentionUsers,
        handleSubmit,

        pendingFiles,
        pendingLinks,
        handleFilesSelected,
        removeFileAttachment,
        handleLinkRequest,
        handleLinkSubmit,
        removeLinkAttachment,

        editPendingFiles,
        editPendingLinks,
        handleEditFilesSelected,
        removeEditFileAttachment,
        handleEditLinkRequest,
        handleEditLinkSubmit,
        removeEditLinkAttachment,

        linkModalOpen,
        setLinkModalOpen,
        linkForm,
        linkCallbackRef,

        editLinkModalOpen,
        setEditLinkModalOpen,
        editLinkForm,
        editLinkCallbackRef,
    } = useProjectSubmissionMemos(projectId, project);

    if (listLoading) {
        return (
            <Container $display="flex" $flex="1" $minHeight="0" $flexDirection="column">
                <EmptyStateBlock>
                    <DetailPanelLoading>
                        <Spin size="large" />
                    </DetailPanelLoading>
                </EmptyStateBlock>
            </Container>
        );
    }

    // ── Shared attachment form section (used in both create & edit modals) ──
    const renderAttachmentFormFields = ({
        formInstance, tLoading, tOptions, mUsers,
        files, links,
        onFilesSelected, onLinkRequest,
        onRemoveFile, onRemoveLink,
        formItemStyle, labelStyle, formStyles,
    }) => (
        <Form form={formInstance} layout="vertical" requiredMark={false} styles={formStyles}>
            <Form.Item
                name="taskIds"
                label={<span style={labelStyle}>Linked Tasks</span>}
                tooltip="Select tasks associated with this submission."
            >
                <Select
                    mode="multiple"
                    placeholder={tLoading ? 'Loading tasks...' : 'Select tasks...'}
                    loading={tLoading}
                    options={tOptions}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    size="large"
                    style={{ width: '100%' }}
                />
            </Form.Item>

            <Form.Item
                name="notes"
                style={formItemStyle}
                label={<span style={labelStyle}>Notes / Description</span>}
                rules={[{ required: true, message: 'Please enter notes for the submission memo' }]}
            >
                <RichTextEditor
                    submissionNeutralLight
                    isAttachment
                    isLink
                    isMention
                    placeholder="Add description — bold, lists, @mentions. Attach files or links."
                    showAttachButton
                    mentionUsers={mUsers}
                    onFilesSelected={onFilesSelected}
                    onLinkRequest={onLinkRequest}
                    attachmentIndexOffset={files.length}
                    onAttachmentRemoved={(index) => {
                        const p = files[index];
                        if (p) onRemoveFile(p.id);
                    }}
                />
            </Form.Item>

            {files.length > 0 && (
                <Container
                    $marginTop="8px"
                    $marginBottom="16px"
                    $padding="8px 0"
                    $borderTop="1px solid var(--ant-color-border, #f0f0f0)"
                >
                    <PanelSectionLabel
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            marginBottom: 8, fontSize: 11,
                            textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.75,
                        }}
                    >
                        <FileOutlined />
                        Attachments
                    </PanelSectionLabel>
                    <Container
                        $display="grid"
                        $gap="10px"
                        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))' }}
                    >
                        {files.map((p) => (
                            <PendingAttachmentTile
                                key={p.id}
                                item={p}
                                onRemove={() => onRemoveFile(p.id)}
                                truncateFileName={truncateFileName}
                            />
                        ))}
                    </Container>
                </Container>
            )}
            {links.length > 0 && (
                <PendingLinkWrap links={links} onRemoveLink={onRemoveLink} />
            )}
        </Form>
    );

    // ── Detail view content (read-only) ──
    const renderDetailContent = () => {
        if (!selectedMemo) return null;
        const memo = selectedMemo;
        const memoTasks = memo.tasks ?? [];
        const memoAttachments = memo.attachments ?? [];
        const notesText = memo.notes?.markdown ?? memo.notes ?? '';

        return (
            <Container $display="flex" $flexDirection="column" $gap="20px" $padding="4px 24px 20px">
                {/* Status & Date */}
                <Container $display="flex" $justifyContent="space-between" $alignItems="center" $flexWrap="wrap" $gap="12px">
                    <Container $display="flex" $alignItems="center" $gap="8px">
                        <StatusBadge status={memo.status ?? 'submitted'} />
                    </Container>
                    <Container $display="flex" $alignItems="center" $gap="6px">
                        <ClockCircleOutlined style={{ fontSize: 12, opacity: 0.5 }} />
                        <MutedText style={{ fontSize: 12, margin: 0 }}>
                            {memo.submittedAt ? formatDate(memo.submittedAt) : memo.createdAt ? formatDate(memo.createdAt) : '—'}
                        </MutedText>
                    </Container>
                </Container>

                {/* Linked Tasks */}
                {memoTasks.length > 0 && (
                    <Container $display="flex" $flexDirection="column" $gap="12px">
                        <PanelSectionLabel style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.65 }}>
                            <FileTextOutlined style={{ marginRight: 6 }} />
                            Linked Tasks ({memoTasks.length})
                        </PanelSectionLabel>
                        <Container $display="grid" $gap="12px" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
                            {memoTasks.map((t) => {
                                const tId = t.taskId ?? t.id ?? t.TaskId ?? t._id;
                                // Add `kanbanColumnTintOnly` and `kanbanColumn="submitted"` so it looks like it belongs to the submission flow.
                                return (
                                    <SprintTaskCard
                                        key={tId}
                                        task={t}
                                        variant="readOnly"
                                        kanbanColumnTintOnly
                                        kanbanColumn="submitted"
                                        onOpenDetail={() => openSprintTaskDetail(t)}
                                    />
                                );
                            })}
                        </Container>
                    </Container>
                )}

                {/* Notes */}
                {notesText && (
                    <Container $display="flex" $flexDirection="column" $gap="6px">
                        <PanelSectionLabel style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.65 }}>
                            Notes / Description
                        </PanelSectionLabel>
                        <Container 
                            $padding="12px 16px" 
                            $borderRadius="8px" 
                            $backgroundColor="var(--ant-color-fill-quaternary, rgba(0, 0, 0, 0.02))"
                            $border="1px solid var(--ant-color-border, #d9d9d9)"
                            style={{ fontSize: 14, lineHeight: 1.6 }}
                        >
                            <Markdown source={notesText} />
                        </Container>
                    </Container>
                )}

                {/* Attachments */}
                {memoAttachments.length > 0 && (
                    <Container $display="flex" $flexDirection="column" $gap="8px" $borderTop="1px solid rgba(255,255,255,0.06)" $paddingTop="12px">
                        <PanelSectionLabel style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', opacity: 0.65 }}>
                            Attachments
                        </PanelSectionLabel>
                        <Container
                            $display="grid"
                            $gap="10px"
                            style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}
                        >
                            {memoAttachments.map((att, index) => {
                                const href = att.url ? resolveAttachmentUrl(att.url) : '';
                                const isImage = isLikelyImageAttachmentUrl(href);
                                const title = att.title || att.name || truncateFileName(att.url?.split('/').pop() || 'Attachment');
                                const isLinkType = att.type === 'link';
                                return (
                                    <a
                                        key={index}
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '10px 12px', borderRadius: 8,
                                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                            border: '1px solid rgba(255, 255, 255, 0.05)',
                                            color: 'inherit', textDecoration: 'none',
                                            transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.06)'; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.03)'; }}
                                    >
                                        {isLinkType
                                            ? <LinkOutlined style={{ color: 'var(--ant-color-primary, #7c3aed)', fontSize: 16 }} />
                                            : <PaperClipOutlined style={{ color: 'var(--ant-color-primary, #7c3aed)', fontSize: 16 }} />
                                        }
                                        <Container $flex="1" $minWidth="0">
                                            <Container style={{ fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {title}
                                            </Container>
                                            <Container style={{ fontSize: 10, opacity: 0.4 }}>
                                                {isLinkType ? 'External link' : 'File attachment'}
                                            </Container>
                                        </Container>
                                    </a>
                                );
                            })}
                        </Container>
                    </Container>
                )}
            </Container>
        );
    };

    return (
        <>
            <ListBodyLayout
                title="Submission Memos"
                description="View submission notes, linked tasks, and work outcomes for this project."
                actions={
                    <Container $display="flex" $alignItems="center" $gap="8px">
                        <TenantToolbarIconBtn
                            type="button"
                            aria-label="Refresh memos"
                            aria-busy={listRefetching}
                            onClick={() => { if (!listRefetching) refetchMemos(); }}
                        >
                            <TenantToolbarRefreshSpin $spin={listRefetching}>
                                <RefreshCwIcon size={18} />
                            </TenantToolbarRefreshSpin>
                        </TenantToolbarIconBtn>
                        <Button
                            type="button"
                            $primary
                            $blackOnLight
                            icon={<PlusOutlined aria-hidden />}
                            onClick={() => setCreateModalOpen(true)}
                            $padding="10px 20px"
                            $size="14px"
                            permissionDenied={!canCreate}
                        >
                            Add Memo
                        </Button>
                    </Container>
                }
                kpiCards={[
                    {
                        label: 'Total Memos',
                        value: totalMemos,
                        icon: <FileTextOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                        trend: 'all time',
                        trendPositive: true,
                    },
                    {
                        label: 'Submitted',
                        value: submittedCount,
                        icon: <CheckCircleOutlined style={{ color: '#10b981' }} />,
                        trend: 'current',
                        trendPositive: true,
                    },
                    {
                        label: 'Linked Tasks',
                        value: totalLinkedTasks,
                        icon: <ClockCircleOutlined style={{ color: '#8b5cf6' }} />,
                        trend: 'across memos',
                        trendPositive: true,
                    },
                    {
                        label: 'Attachments',
                        value: totalAttachments,
                        icon: <PaperClipOutlined style={{ color: '#f59e0b' }} />,
                        trend: 'files & links',
                        trendPositive: true,
                    },
                ]}
            >
                <Table
                    columns={columns}
                    dataSource={tableData}
                    pagination
                    onRow={(record) => ({
                        onClick: () => openDetailModal(record._raw),
                        style: { cursor: 'pointer' },
                    })}
                />
            </ListBodyLayout>

            {/* ── Create Modal ── */}
            <AppModal
                size="md"
                title="Add Submission Memo"
                open={createModalOpen}
                onCancel={closeCreateModal}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        <Button $primary={false} $size="14px" onClick={closeCreateModal}>Cancel</Button>
                        <Button
                            $primary $blackOnLight $size="14px"
                            loading={createMutation.isPending}
                            icon={<SendOutlined />}
                            onClick={handleSubmit}
                        >
                            Create Memo
                        </Button>
                    </CreateRoleModalFooter>
                }
            >
                <CreateRoleModalBody $flush $noMaxHeight>
                    {renderAttachmentFormFields({
                        formInstance: form,
                        tLoading: tasksLoading,
                        tOptions: taskOptions,
                        mUsers: mentionUsers,
                        files: pendingFiles,
                        links: pendingLinks,
                        onFilesSelected: handleFilesSelected,
                        onLinkRequest: handleLinkRequest,
                        onRemoveFile: removeFileAttachment,
                        onRemoveLink: removeLinkAttachment,
                        formItemStyle: submissionRichDescriptionFormItemStyle,
                        labelStyle: submissionRichDescriptionLabelStyle,
                        formStyles: projectSprintTaskModalFormStyles,
                    })}
                </CreateRoleModalBody>
            </AppModal>

            {/* ── Detail / Edit Modal ── */}
            <AppModal
                size="md"
                title={isEditing ? 'Edit Submission Memo' : 'Submission Memo'}
                open={detailModalOpen}
                onCancel={closeDetailModal}
                footer={
                    <CreateRoleModalFooter $inFooterSlot>
                        {isEditing ? (
                            <>
                                <Button $primary={false} $size="14px" onClick={closeDetailModal}>Cancel</Button>
                                <Button
                                    $primary $blackOnLight $size="14px"
                                    loading={updateMutation.isPending}
                                    icon={<SendOutlined />}
                                    onClick={handleEditSubmit}
                                >
                                    Save Changes
                                </Button>
                            </>
                        ) : (
                            <>
                                <Container $display="flex" $alignItems="center" $gap="8px">
                                    <Popconfirm
                                        title="Delete this submission memo?"
                                        description="This action cannot be undone."
                                        onConfirm={handleDelete}
                                        okText="Delete"
                                        cancelText="Cancel"
                                        okButtonProps={{ danger: true }}
                                    >
                                        <Button
                                            $primary={false}
                                            $size="14px"
                                            icon={<DeleteOutlined />}
                                            permissionDenied={!canDelete}
                                            loading={deleteMutation.isPending}
                                        >
                                            Delete
                                        </Button>
                                    </Popconfirm>
                                </Container>
                                <Container $display="flex" $alignItems="center" $gap="8px">
                                    <Button $primary={false} $size="14px" onClick={closeDetailModal}>Close</Button>
                                    <Button
                                        $primary $blackOnLight $size="14px"
                                        icon={<EditOutlined />}
                                        onClick={startEditing}
                                        permissionDenied={!canUpdate}
                                    >
                                        Edit
                                    </Button>
                                </Container>
                            </>
                        )}
                    </CreateRoleModalFooter>
                }
            >
                {isEditing ? (
                    <CreateRoleModalBody $flush $noMaxHeight>
                        {renderAttachmentFormFields({
                            formInstance: editForm,
                            tLoading: tasksLoading,
                            tOptions: taskOptions,
                            mUsers: mentionUsers,
                            files: editPendingFiles,
                            links: editPendingLinks,
                            onFilesSelected: handleEditFilesSelected,
                            onLinkRequest: handleEditLinkRequest,
                            onRemoveFile: removeEditFileAttachment,
                            onRemoveLink: removeEditLinkAttachment,
                            formItemStyle: submissionRichDescriptionFormItemStyle,
                            labelStyle: submissionRichDescriptionLabelStyle,
                            formStyles: projectSprintTaskModalFormStyles,
                        })}
                    </CreateRoleModalBody>
                ) : (
                    renderDetailContent()
                )}
            </AppModal>

            {/* ── Create Link Modal ── */}
            <AddUrlModal
                open={linkModalOpen}
                onCancel={() => { setLinkModalOpen(false); linkCallbackRef.current = null; }}
                form={linkForm}
                onSubmit={handleLinkSubmit}
                title="Add Link"
            />

            {/* ── Edit Link Modal ── */}
            <AddUrlModal
                open={editLinkModalOpen}
                onCancel={() => { setEditLinkModalOpen(false); editLinkCallbackRef.current = null; }}
                form={editLinkForm}
                onSubmit={handleEditLinkSubmit}
                title="Add Link"
            />

            {/* ── Task Detail Modal ── */}
            <TaskDetailModal
                projectId={projectId}
                open={Boolean(sprintTaskDetail?.task && sprintTaskDetail?.sprint)}
                onClose={closeSprintTaskDetail}
                onRequestRefresh={refreshSprintTaskDetail}
                task={sprintTaskDetail?.task ?? null}
                taskDescriptionMarkdown={sprintTaskDetailMarkdown}
                taskBugs={sprintTaskDetailBugs}
                mentionUsers={mentionUsers}
                readOnlyBugActions
            />
        </>
    );
}
